import { UTM } from "../map/projection.js";
import { Hemisphere, LatLng, Point2D } from "../map/types.js";
import { BufferedScalarField } from "./implementations.js";
class ScalarFloatField2DSourceFactory {
    textDecoder = new TextDecoder();
    create(dataView, offset) {
        //start after format identifier
        let i = offset + 4 + Int16Array.BYTES_PER_ELEMENT + this.getName().length;
        //parse the header and extract the data
        let propertiesParseResult = this.parseHeader(dataView, i);
        i = propertiesParseResult.offset;
        let properties = propertiesParseResult.properties;
        const dataSize = properties.size.x * properties.size.y * Float32Array.BYTES_PER_ELEMENT;
        let data = new Float32Array(dataView.buffer.slice(i, i + dataSize));
        i += dataSize;
        //console.log('buffer.byteLength = ',  dataView.byteLength, ' last index =  ', i);
        //console.log('from ',  (new Date(properties.start).toISOString()),  ' to  ',(new Date(properties.end).toISOString()));
        //console.log('data =  ',data);
        //construct the scalarFieldSource
        const utm = UTM.get(properties.zone, (properties.hemisphere === 'N' ? Hemisphere.North : Hemisphere.South));
        //add source location in geodetic form
        if (properties.sourceUtm) {
            properties.sourceLatLng = utm.toGeodetic([properties.sourceUtm.x, properties.sourceUtm.y]);
        }
        //we need to get our bearings straight. What is up what is down? What is left what is right?
        let nativeIterationBounds = {
            rowOrientation: properties.rowOrientation,
            x: {
                min: properties.step.x > 0 ? properties.first.x : properties.first.x + (properties.size.x - 1) * (properties.step.x),
                max: 0,
                step: Math.abs(properties.step.x), //always positive
                count: properties.size.x
            },
            y: {
                min: 0,
                max: properties.step.y < 0 ? properties.first.y : properties.first.y + (properties.size.y - 1) * (properties.step.y),
                step: -1 * Math.abs(properties.step.y), //always negative 
                count: properties.size.y
            }
        };
        nativeIterationBounds.x.max = nativeIterationBounds.x.min + (properties.size.x - 1) * nativeIterationBounds.x.step;
        nativeIterationBounds.y.min = nativeIterationBounds.y.max + (properties.size.y - 1) * nativeIterationBounds.y.step;
        //console.info('RANGE=%o',properties.range);
        let scalarFieldSource = new BufferedScalarField(properties.projectId, utm, nativeIterationBounds, { start: properties.start, end: properties.end }, properties.step, Array.prototype.slice.call(data), 0.0, `${properties.projectId}/${properties.runId}_${properties.start}_${properties.end}`, properties.unit, properties);
        return { offset: i, scalarField: scalarFieldSource };
    }
    parseHeader(dataView, offset) {
        let i = offset;
        let properties = {};
        properties.domainId = this.textDecoder.decode(new Uint8Array(dataView.buffer.slice(i, i + 36)));
        //properties.domainId = String.fromCharCode.apply(null, new Uint8Array(dataView.buffer.slice(i, i + 36)));
        i += 36;
        properties.projectId = this.textDecoder.decode(new Uint8Array(dataView.buffer.slice(i, i + 36)));
        //properties.projectId = String.fromCharCode.apply(null, new Uint8Array(dataView.buffer.slice(i, i + 36)));
        i += 36;
        properties.runId = this.textDecoder.decode(new Uint8Array(dataView.buffer.slice(i, i + 36)));
        //properties.runId = String.fromCharCode.apply(null, new Uint8Array(dataView.buffer.slice(i, i + 36)));
        i += 36;
        properties.start = dataView.getFloat64(i, true);
        i += Float64Array.BYTES_PER_ELEMENT;
        properties.end = dataView.getFloat64(i, true);
        i += Float64Array.BYTES_PER_ELEMENT;
        const timeZoneLength = dataView.getInt16(i, true);
        i += Int16Array.BYTES_PER_ELEMENT;
        properties.timeZone = this.textDecoder.decode(new Uint8Array(dataView.buffer.slice(i, i + timeZoneLength)));
        //properties.timeZone = String.fromCharCode.apply(null, new Uint8Array(dataView.buffer.slice(i, i + timeZoneLength)));
        i += timeZoneLength;
        var parameterBlockByteSize = dataView.getInt16(i, true);
        i += Int16Array.BYTES_PER_ELEMENT;
        i = this.parseModelParameters(dataView, i, properties, parameterBlockByteSize);
        let num0, num1;
        properties.zone = dataView.getInt8(i);
        i += 1;
        properties.hemisphere = this.textDecoder.decode(new Uint8Array(dataView.buffer.slice(i, i + 1)));
        //properties.hemisphere = String.fromCharCode.apply(null, new Uint8Array(dataView.buffer.slice(i, i + 1)));
        i += 1;
        const unitLength = dataView.getInt16(i, true);
        i += Int16Array.BYTES_PER_ELEMENT;
        properties.unit = this.textDecoder.decode(new Uint8Array(dataView.buffer.slice(i, i + unitLength)));
        //properties.unit = String.fromCharCode.apply(null, new Uint8Array(dataView.buffer.slice(i, i + unitLength)));
        i += unitLength;
        properties.rowOrientation = dataView.getInt8(i) !== 0;
        i += 1;
        num0 = dataView.getFloat64(i, true);
        i += Float64Array.BYTES_PER_ELEMENT;
        num1 = dataView.getFloat64(i, true);
        i += Float64Array.BYTES_PER_ELEMENT;
        properties.first = { x: num0, y: num1 };
        num0 = dataView.getFloat64(i, true);
        i += Float64Array.BYTES_PER_ELEMENT;
        num1 = dataView.getFloat64(i, true);
        i += Float64Array.BYTES_PER_ELEMENT;
        properties.step = { x: num0, y: num1 };
        num0 = dataView.getInt32(i, true);
        i += Int32Array.BYTES_PER_ELEMENT;
        num1 = dataView.getInt32(i, true);
        i += Int32Array.BYTES_PER_ELEMENT;
        properties.size = { x: num0, y: num1 };
        num0 = dataView.getFloat32(i, true);
        i += Float32Array.BYTES_PER_ELEMENT;
        num1 = dataView.getFloat32(i, true);
        i += Float32Array.BYTES_PER_ELEMENT;
        properties.range = { min: num0, max: num1 };
        return { offset: i, properties: properties };
    }
    ;
}
class ScalarFloatField2DSourceFactory_v1_0_1 extends ScalarFloatField2DSourceFactory {
    static NAME = 'sff2d_v1.0.1';
    getName() {
        return ScalarFloatField2DSourceFactory_v1_0_1.NAME;
    }
    parseModelParameters(dataView, offset, properties, blockSize) {
        return parseGausianPlumeUTM_V1_0_0(dataView, offset, properties, this.textDecoder);
    }
    ;
}
class ScalarFloatField2DSourceFactory_v1_0_2 extends ScalarFloatField2DSourceFactory {
    static NAME = 'sff2d_v1.0.2';
    getName() {
        return ScalarFloatField2DSourceFactory_v1_0_2.NAME;
    }
    parseModelParameters(dataView, offset, properties, blockSize) {
        if (blockSize > 0) {
            let i = offset;
            const modelNameLength = dataView.getInt16(i, true);
            i += Int16Array.BYTES_PER_ELEMENT;
            let model = this.textDecoder.decode(new Uint8Array(dataView.buffer.slice(i, i + modelNameLength)));
            //let model = String.fromCharCode.apply(null, new Uint8Array(dataView.buffer.slice(i, i + modelNameLength)));
            i += modelNameLength;
            if (model === 'gaussian-plume-utm_v1.0.0') {
                return parseGausianPlumeUTM_V1_0_0(dataView, i, properties, this.textDecoder);
            }
            else if (model === 'calpuff_v1.0.0') {
                return parseCalpuff_V1_0_0(dataView, i, properties, this.textDecoder);
            }
            else {
                console.warn(`Encountered ScalarFloatField2DSource for unknown model ${model}. Skipped parsing model parameters.`);
                return offset + blockSize;
            }
        }
        else {
            //console.info(`No model parameters found.`);
            return offset + blockSize;
        }
    }
}
const parseGausianPlumeUTM_V1_0_0 = (dataView, offset, properties, textDecoder) => {
    let i = offset;
    const sourceUtmX = dataView.getFloat64(i, true);
    i += Float64Array.BYTES_PER_ELEMENT;
    const sourceUtmY = dataView.getFloat64(i, true);
    i += Float64Array.BYTES_PER_ELEMENT;
    properties.sourceUtm = new Point2D(sourceUtmX, sourceUtmY);
    properties.windDirection = dataView.getFloat64(i, true);
    i += Float64Array.BYTES_PER_ELEMENT;
    properties.windVelocity = dataView.getFloat64(i, true);
    i += Float64Array.BYTES_PER_ELEMENT;
    properties.stability = textDecoder.decode(new Uint8Array(dataView.buffer.slice(i, i + 1)));
    //properties.stability = String.fromCharCode.apply(null, new Uint8Array(dataView.buffer.slice(i, i + 1)));
    i += 1;
    properties.emissionRate = dataView.getFloat64(i, true);
    i += Float64Array.BYTES_PER_ELEMENT;
    properties.heatCapacity = dataView.getFloat64(i, true);
    i += Float64Array.BYTES_PER_ELEMENT;
    properties.terrainRoughness = dataView.getFloat64(i, true);
    i += Float64Array.BYTES_PER_ELEMENT;
    properties.sourceHeight = dataView.getFloat64(i, true);
    i += Float64Array.BYTES_PER_ELEMENT;
    properties.targetHeight = dataView.getFloat64(i, true);
    i += Float64Array.BYTES_PER_ELEMENT;
    properties.averagingPeriod = dataView.getFloat64(i, true);
    i += Float64Array.BYTES_PER_ELEMENT;
    return i;
};
const parseCalpuff_V1_0_0 = (dataView, offset, properties, textDecoder) => {
    let i = offset;
    properties.stackExitSpeed = dataView.getFloat32(i, true);
    i += Float32Array.BYTES_PER_ELEMENT;
    properties.stackExitTemperature = dataView.getFloat32(i, true);
    i += Float32Array.BYTES_PER_ELEMENT;
    properties.emissionRate = dataView.getFloat32(i, true);
    i += Float32Array.BYTES_PER_ELEMENT;
    const specieCodeLength = dataView.getInt16(i, true);
    i += Int16Array.BYTES_PER_ELEMENT;
    properties.specieCode = textDecoder.decode(new Uint8Array(dataView.buffer.slice(i, i + specieCodeLength)));
    //properties.specieCode = String.fromCharCode.apply(null, new Uint8Array(dataView.buffer.slice(i, i + specieCodeLength)));
    i += specieCodeLength;
    const specieUnitLength = dataView.getInt16(i, true);
    i += Int16Array.BYTES_PER_ELEMENT;
    properties.specieUnit = textDecoder.decode(new Uint8Array(dataView.buffer.slice(i, i + specieUnitLength)));
    //properties.specieUnit = String.fromCharCode.apply(null, new Uint8Array(dataView.buffer.slice(i, i + specieUnitLength)));
    i += specieUnitLength;
    properties.sourceId = textDecoder.decode(new Uint8Array(dataView.buffer.slice(i, i + 36)));
    //properties.sourceId = String.fromCharCode.apply(null, new Uint8Array(dataView.buffer.slice(i, i + 36)));
    i += 36;
    return i;
};
const FACTORIES = {};
FACTORIES[ScalarFloatField2DSourceFactory_v1_0_1.NAME] = new ScalarFloatField2DSourceFactory_v1_0_1();
FACTORIES[ScalarFloatField2DSourceFactory_v1_0_2.NAME] = new ScalarFloatField2DSourceFactory_v1_0_2();
export const scalarFieldProducer = {
    getScalarFields: (blob) => {
        var scalarFields = [];
        if (blob != null && (blob.byteLength !== null && blob.byteLength > 0)) {
            let dataView = new DataView(blob);
            let i = 0;
            let factoryResult;
            let format;
            try {
                while (i < blob.byteLength) {
                    format = getFormat(dataView, i);
                    if (format) {
                        if (FACTORIES[format]) {
                            factoryResult = FACTORIES[format].create(dataView, i);
                            i = factoryResult.offset;
                            scalarFields.push(factoryResult.scalarField);
                        }
                        else {
                            throw `Unable to parse/eat unknown format ${format}.`;
                        }
                    }
                    else {
                        throw `Unable to parse/eat unknown format ${format}.`;
                    }
                }
            }
            catch (x) {
                console.error(x);
            }
        }
        return scalarFields;
    }
};
const getFormat = (dataView, offset) => {
    let i = offset;
    let format = undefined;
    const textDecoder = new TextDecoder();
    let prefix = textDecoder.decode(new Uint8Array(dataView.buffer.slice(i, i + 4)));
    //let prefix: string = String.fromCharCode.apply(null, new Uint8Array(dataView.buffer.slice(i, i + 4)));
    if (prefix === 'tlmd') {
        i += 4;
        const formatNameLength = dataView.getInt16(i, true);
        i += Int16Array.BYTES_PER_ELEMENT;
        format = textDecoder.decode(new Uint8Array(dataView.buffer.slice(i, i + formatNameLength)));
        //format = String.fromCharCode.apply(null, new Uint8Array(dataView.buffer.slice(i, i + formatNameLength)));
    }
    return format;
};
